import {
  get,
  isDate,
  isFunction,
  isNumber,
  isString,
  orderBy,
  toString,
} from "lodash";
import moment from "moment-timezone";
import { Locale } from "@linkfluence/models/locale";

type List<T> = T[];
type AbstractNatural = string | number;

// amount of extra zeros to padd for sorting
const padding = (value: string) => "00000000000000000000".slice(value.length);

function getDate<T>(date: T): number {
  if (isString(date) || isDate(date) || isNumber(date)) {
    return moment(date).valueOf();
  } else if (moment.isMoment(date)) {
    return date.valueOf();
  } else {
    return moment().valueOf();
  }
}

// Fix numbers to be correctly padded
const fixNumbers = (value: string) =>
  value.replace(/(\d+)((\.\d+)+)?/g, ($0, integer, decimal, $3) => {
    // If there's more than 2 sets of numbers...
    if (decimal !== $3) {
      // treat as a series of integers, like versioning,
      // rather than a decimal
      return $0.replace(/(\d+)/g, ($d) => padding($d) + $d);
    } else {
      // add a decimal if necessary to ensure decimal sorting
      decimal = decimal || ".0";
      return padding(integer) + integer + decimal + padding(decimal);
    }
  });

const natValue = (value: AbstractNatural) => {
  if (isNumber(value)) {
    value = toString(value);
  } else {
    value = "" + (value || "");
  }
  return fixNumbers(value.toLowerCase());
};

const naturalSort = (as: AbstractNatural, bs: AbstractNatural) => {
  const a = natValue(as);
  const b = natValue(bs);
  return a.localeCompare(b, "en") < 0
    ? -1
    : a.localeCompare(b, "en") > 0
    ? 1
    : 0;
};

export function natural<T>(
  collection: List<T> = [],
  predicat?: ((entry: T) => AbstractNatural) | string
): List<T> {
  return collection.slice().sort((a, b) => {
    if (predicat) {
      if (isFunction(predicat)) {
        return naturalSort(predicat(a), predicat(b));
      } else {
        return naturalSort(get(a, predicat), get(b, predicat));
      }
    } else if ((isString(a) || isNumber(a)) && (isString(b) || isNumber(b))) {
      return naturalSort(a, b);
    } else {
      return 1;
    }
  });
}

function getCollatorOptions<T extends object | number | string>(
  data: T[],
  options?: Intl.CollatorOptions
): Intl.CollatorOptions {
  const defaultOptions = options || { sensitivity: "variant" };
  if (data.some((d) => !isNaN(d as number))) {
    return { ...defaultOptions, numeric: true };
  }
  return defaultOptions;
}

// only use if performance is a concern. Else, natural() is fine.
export function localeSort<T extends object | number | string>(
  data: T[] = [],
  locale: Locale,
  options?: Intl.CollatorOptions,
  keySort?: string
) {
  const collatorOptions = getCollatorOptions(data, options);
  const collator = new Intl.Collator(locale.split("_")[0], collatorOptions);
  if (typeof data[0] === "object" && keySort) {
    return data.sort((a, b) => collator.compare(a[keySort], b[keySort]));
  }
  if (typeof data[0] === "number" || typeof data[0] === "string") {
    return data.sort((a, b) => collator.compare(a as string, b as string));
  }
  return data;
}

export function date<T>(
  collection: List<T>,
  predicat?: ((entry: T) => any) | string,
  order?: "asc" | "desc"
): List<T> {
  return orderBy(
    collection,
    (entry) => {
      if (predicat) {
        if (isFunction(predicat)) {
          return getDate(predicat(entry));
        } else {
          return getDate(get(entry, predicat));
        }
      } else {
        return getDate(entry);
      }
    },
    order
  );
}

export function predefinedSort<T extends Record<string, any>>(
  collection: List<T>,
  predicat: string,
  predefinedOrder: Array<string | number>
): List<T> {
  return collection.sort(function (a, b) {
    return (
      predefinedOrder.indexOf(a[predicat]) -
      predefinedOrder.indexOf(b[predicat])
    );
  });
}
